import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { progressGoals } from '../data/workoutPlans';
import { SkeletonCard } from '../components/SkeletonLoader';
import EmptyState from '../components/EmptyState';
import './Progress.css';

function Progress() {
  const navigate = useNavigate();
  const [workoutHistory, setWorkoutHistory] = useState([]);
  const [measurements, setMeasurements] = useState({
    weight: '175',
    bodyFat: '20',
    arms: '',
    chest: '',
    shoulders: '',
    thighs: '',
    pullUps: '3',
    pushUps: '15',
  });
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = () => {
    setIsLoading(true);
    setError(null);
    try {
      const history = localStorage.getItem('workoutHistory');
      const savedMeasurements = localStorage.getItem('measurements');

      if (history) {
        setWorkoutHistory(JSON.parse(history));
      }
      if (savedMeasurements) {
        setMeasurements(JSON.parse(savedMeasurements));
      }
    } catch (error) {
      console.error('Error loading data:', error);
      setError('Failed to load your progress data. Please refresh the page.');
    } finally {
      setIsLoading(false);
    }
  };

  const saveMeasurements = () => {
    try {
      localStorage.setItem('measurements', JSON.stringify(measurements));

      const measurementHistory = localStorage.getItem('measurementHistory');
      const history = measurementHistory ? JSON.parse(measurementHistory) : [];
      history.push({
        date: new Date().toISOString(),
        ...measurements,
      });
      localStorage.setItem('measurementHistory', JSON.stringify(history));

      setIsEditing(false);
      alert('Measurements saved!');
    } catch (error) {
      console.error('Error saving measurements:', error);
    }
  };

  const getWorkoutsThisWeek = () => {
    const oneWeekAgo = new Date();
    oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);

    return workoutHistory.filter(workout => {
      const workoutDate = new Date(workout.date);
      return workoutDate >= oneWeekAgo;
    }).length;
  };

  return (
    <div className="container">
      <div className="progress-screen">
        <div className="progress-header">
          <button className="back-button" onClick={() => navigate('/')}>
            ← Back
          </button>
          <h2>Progress Tracker</h2>
        </div>

        {error && (
          <div className="error-message" style={{
            backgroundColor: 'rgba(255, 107, 53, 0.1)',
            border: '2px solid #FF6B35',
            borderRadius: '12px',
            padding: '20px',
            marginBottom: '20px',
            textAlign: 'center'
          }}>
            <p style={{ color: '#FF6B35', margin: 0 }}>{error}</p>
            <button
              onClick={loadData}
              style={{
                marginTop: '15px',
                padding: '10px 20px',
                backgroundColor: '#FF6B35',
                color: '#fff',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer'
              }}
            >
              Retry
            </button>
          </div>
        )}

        {isLoading ? (
          <>
            <SkeletonCard />
            <SkeletonCard />
            <SkeletonCard />
          </>
        ) : error ? null : workoutHistory.length === 0 ? (
          <EmptyState
            icon="💪"
            title="No Workouts Yet"
            message="Start your first workout to begin tracking your progress and building the body you deserve."
            actionText="Go to Home"
            onAction={() => navigate('/')}
          />
        ) : (
          <>
        {/* Workout Stats */}
        <div className="section">
          <h3>Workout History</h3>
          <div className="stats-grid">
            <div className="stat-card">
              <p className="stat-value">{getWorkoutsThisWeek()}</p>
              <p className="stat-label">This Week</p>
            </div>
            <div className="stat-card">
              <p className="stat-value">{workoutHistory.length}</p>
              <p className="stat-label">Total Workouts</p>
            </div>
          </div>
        </div>

        {/* Measurements */}
        <div className="section">
          <div className="section-header">
            <h3>Current Measurements</h3>
            <button className="edit-button" onClick={() => setIsEditing(!isEditing)}>
              {isEditing ? 'Cancel' : 'Edit'}
            </button>
          </div>

          <div className="measurement-card">
            {[
              { key: 'weight', label: 'Weight', unit: 'lbs' },
              { key: 'bodyFat', label: 'Body Fat %', unit: '%' },
              { key: 'arms', label: 'Arms (circumference)', unit: 'inches' },
              { key: 'chest', label: 'Chest', unit: 'inches' },
              { key: 'shoulders', label: 'Shoulders', unit: 'inches' },
              { key: 'thighs', label: 'Thighs', unit: 'inches' },
            ].map(({ key, label, unit }) => (
              <div key={key} className="measurement-row">
                <span className="measurement-label">{label}</span>
                {isEditing ? (
                  <input
                    type="number"
                    className="measurement-input"
                    value={measurements[key]}
                    onChange={(e) => setMeasurements({...measurements, [key]: e.target.value})}
                    placeholder="--"
                  />
                ) : (
                  <span className="measurement-value">
                    {measurements[key] || '--'} {measurements[key] && unit}
                  </span>
                )}
              </div>
            ))}
          </div>

          {isEditing && (
            <button className="save-button" onClick={saveMeasurements}>
              Save Measurements
            </button>
          )}
        </div>

        {/* Strength Progress */}
        <div className="section">
          <h3>Strength Progress</h3>
          <div className="measurement-card">
            <div className="measurement-row">
              <span className="measurement-label">Pull-ups (max reps)</span>
              {isEditing ? (
                <input
                  type="number"
                  className="measurement-input"
                  value={measurements.pullUps}
                  onChange={(e) => setMeasurements({...measurements, pullUps: e.target.value})}
                />
              ) : (
                <span className="measurement-value">{measurements.pullUps} reps</span>
              )}
            </div>
            <div className="goal-row">
              <span>Goal (Week 4): {progressGoals.pullUps.week4} reps</span>
            </div>

            <div className="measurement-row">
              <span className="measurement-label">Push-ups (max reps)</span>
              {isEditing ? (
                <input
                  type="number"
                  className="measurement-input"
                  value={measurements.pushUps}
                  onChange={(e) => setMeasurements({...measurements, pushUps: e.target.value})}
                />
              ) : (
                <span className="measurement-value">{measurements.pushUps} reps</span>
              )}
            </div>
            <div className="goal-row">
              <span>Goal (Week 4): {progressGoals.pushUps.week4} reps</span>
            </div>
          </div>
        </div>

        {/* Goals */}
        <div className="section">
          <h3>12-Week Goals</h3>
          <div className="goals-card">
            {[
              { icon: '💪', text: '10 strict pull-ups' },
              { icon: '🔥', text: '30 push-ups in one set' },
              { icon: '📏', text: '+1 inch on arms' },
              { icon: '🎯', text: '15% body fat' },
              { icon: '💎', text: 'Firmer, more defined chest' },
            ].map((goal, i) => (
              <div key={i} className="goal-item">
                <span className="goal-icon">{goal.icon}</span>
                <span className="goal-text">{goal.text}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Achievements */}
        <div className="section">
          <h3>Your Journey</h3>
          <div className="achievement-card">
            <h4>🏆 Already Achieved</h4>
            <p>• Lost 60 lbs (233 → 175 lbs)</p>
            <p>• Reached 20% body fat</p>
            <p>• Built the discipline to transform</p>
          </div>
        </div>
        </>
        )}
      </div>
    </div>
  );
}

export default Progress;
